import { useMemo } from "react";

import { useGetContent, useGetSessionVisibility } from "../../../api/insights";
import { useGetAllFeatureFlags } from "../../../api/featureFlags";
import { FEATURE_FLAGS } from "@congrs/schemas";
import { DailyAreaChart, RankedBarChart, formatDay } from "../charts";
import SectionShell, { Stat, StatGrid, formatNum } from "../SectionShell";
import { useChartTimeFrame } from "../useChartTimeFrame";
import { sliceByDay, toRangeParams } from "../timeFrame";

function AgendaUsage({ eventId }) {
  const { data, isPending, isError, error } = useGetContent(eventId);
  const { data: flags } = useGetAllFeatureFlags({ eventId });

  const tf = useChartTimeFrame(eventId);
  const sessionsTf = useChartTimeFrame(eventId);
  const { data: sessionData } = useGetSessionVisibility(
    eventId,
    toRangeParams(sessionsTf.range)
  );

  const enabledFlagSet = useMemo(
    () =>
      new Set(
        (Array.isArray(flags) ? flags : [])
          .filter((f) => f.enabled)
          .map((f) => f.featureName)
      ),
    [flags]
  );

  const showHighestRated = enabledFlagSet.has(FEATURE_FLAGS.SESSION_RATINGS);

  const topSessions = useMemo(
    () =>
      (sessionData?.mostViewedSessions ?? []).map((s) => ({
        session: s.sessionName ?? "(unknown)",
        Views: s.viewCount,
        "Unique users": s.uniqueUsers,
        "Link clicks": s.linkClicks ?? 0,
      })),
    [sessionData]
  );
  const hasSessionLinkClicks = topSessions.some((s) => s["Link clicks"] > 0);

  const agendaOverTime = useMemo(
    () =>
      sliceByDay(data?.agendaViewsOverTime, tf.resolved).map((d) => ({
        day: formatDay(d.day),
        Views: d.viewCount,
        "Unique users": d.uniqueUsers,
      })),
    [data, tf.resolved]
  );

  const topRated = useMemo(
    () =>
      (data?.highestRatedSessions ?? []).map((s) => ({
        session: `${s.sessionName ?? "(unknown)"} (${s.ratingCount})`,
        Rating: s.rating,
      })),
    [data]
  );

  return (
    <SectionShell
      id="agendaUsage"
      title="Agenda usage"
      description="Session views, bookmarks and how many sessions an attendee follows on average."
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="agendaUsage"
    >
      <StatGrid>
        <Stat
          part="bookmarks"
          label="Bookmarks"
          value={formatNum(data?.totalBookmarks)}
        />
        <Stat
          part="activeAppUsers"
          label="Active app users"
          value={formatNum(data?.totalAppUsers)}
        />
        <Stat
          part="avgSessionsFollowed"
          label="Avg sessions followed"
          value={
            Number.isFinite(Number(data?.avgSessionsFollowed))
              ? Number(data.avgSessionsFollowed).toFixed(2)
              : "—"
          }
        />
        <Stat
          part="sessionsTracked"
          label="Sessions tracked"
          value={formatNum(data?.sessionsTracked)}
        />
      </StatGrid>

      <RankedBarChart
        part="mostViewedSessions"
        title="Most viewed sessions"
        data={topSessions}
        categoryKey="session"
        series={[
          ...(hasSessionLinkClicks
            ? [{ name: "Link clicks", color: "violet.3", stackId: "totals" }]
            : []),
          { name: "Unique users", color: "teal.6", stackId: "totals" },
          { name: "Views", color: "blue.6", stackId: "totals" },
        ]}
        headerRight={sessionsTf.control}
      />

      <DailyAreaChart
        part="agendaViewsOverTime"
        title="Agenda views over time"
        data={agendaOverTime}
        series={[
          { name: "Views", color: "blue.6" },
          { name: "Unique users", color: "teal.6" },
        ]}
        headerRight={tf.control}
      />

      {showHighestRated && (
        <RankedBarChart
          part="highestRatedSessions"
          title="Highest rated sessions"
          data={topRated}
          categoryKey="session"
          series={[{ name: "Rating", color: "blue.6" }]}
          xAxisProps={{ domain: [0, 5], ticks: [0, 1, 2, 3, 4, 5] }}
        />
      )}
    </SectionShell>
  );
}

export default AgendaUsage;
